import type { INotification } from "@/lib/utils";

import { defaultNotificationConfig, NotificationPlacement, NotificationStatus } from "@/lib/utils";
import { reactive } from "vue";

export type GlobalStoreState = {
  baseNotificationTimeout?: ReturnType<typeof setTimeout>,
  isMobileView: boolean,
  isNotificationVisible: boolean,
  isRouteLoading: boolean,
  isTabletView: boolean,
  notificationConfig: INotification,
  screenSize: {
    height: number,
    width: number,
  },
  showGlobalLoader: boolean,
}

export type GlobalStore = {
  getIsMobileView: typeof getIsMobileView,
  getIsTabletView: typeof getIsTabletView,
  getNotificationConfig: typeof getNotificationConfig,
  getScreenSize: typeof getScreenSize,
  hideGlobalLoader: typeof hideGlobalLoader,
  hideNotification: typeof hideNotification,
  initOnResize: typeof initOnResize,
  setScreenSize: typeof setScreenSize,
  showGlobalLoader: typeof showGlobalLoader,
  showNotification: typeof showNotification,
  showRouteLoader: typeof showRouteLoader,
  state: GlobalStoreState
}

const state: GlobalStoreState = reactive({
  baseNotificationTimeout: undefined,
  isMobileView: window.innerWidth < 644,
  isNotificationVisible: false,
  isRouteLoading: true,
  isTabletView: window.innerWidth < 1200,
  notificationConfig: {
    closeable: true,
    duration: 3000,
    placement: NotificationPlacement.TOP,
    type: NotificationStatus.SUCCESS,
  } as INotification,
  screenSize: {
    height: window.innerHeight,
    width: window.innerWidth,
  },
  showGlobalLoader: false,
});


const setScreenSize = (width: number, height: number) => {
  state.screenSize =  {
    height,
    width,
  };
  state.isMobileView = width < 644;
  state.isTabletView = width < 1200;
};

const getScreenSize = () => state.screenSize;

const getNotificationConfig = () => state.notificationConfig;

const hideNotification = () => {
  state.isNotificationVisible = false;
  state.notificationConfig = {
    ...defaultNotificationConfig,
  };
  clearTimeout(state.baseNotificationTimeout);
};

const getIsMobileView = () => state.isMobileView;

const getIsTabletView = () => state.isTabletView;

const initOnResize = () => {
  window.addEventListener("resize", () => {
    setScreenSize(window.innerWidth, window.innerHeight);
  });
};

const showNotification = (config: INotification) => {
  state.notificationConfig = {
    ...state.notificationConfig,
    ...config,
  };
  state.isNotificationVisible = true;
  if (state.notificationConfig.closeable) {
    state.baseNotificationTimeout = setTimeout(() => {
      hideNotification();
    }, state.notificationConfig.duration);
  }
};

const showRouteLoader = () => {
  state.isRouteLoading = true;
};

const showGlobalLoader = () => {
  state.showGlobalLoader = true;
};

const hideGlobalLoader = () => {
  state.showGlobalLoader = false;
};

export default {
  getIsMobileView,
  getIsTabletView, 
  getNotificationConfig,
  getScreenSize,
  hideGlobalLoader,
  hideNotification,
  initOnResize,
  setScreenSize,
  showGlobalLoader,
  showNotification,
  showRouteLoader,
  state,
};
